import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, cn } from '@saas-pages/shared/ui'
import { startToolOperation, type ToolAnalyticsContext } from '@saas-pages/shared/public/runtime'
import { useRouterState } from '@tanstack/react-router'
import {
  numberLocale,
  pilotAnalyticsDimensions,
  useLocale,
  useT,
} from '@/shared/i18n'
import { formatJson, minifyJson, validateJson } from '@/shared/json'
import { ToolPageShell } from '@/shared/tool-shell/ToolPageShell'
import { CodeWorkspace } from '@/shared/ui/code-workspace'

type OutputMode = 'format' | 'minify' | 'validate'

export function JsonFormatterPage() {
  const t = useT()
  const { locale } = useLocale()
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const localeCode = numberLocale(locale)
  const analyticsContext = useMemo(() => {
    const dimensions = pilotAnalyticsDimensions(pathname, locale)
    return {
      app: 'devdeck',
      domain: 'devdeck.ru',
      route: dimensions.route,
      utility: 'json-formatter',
      processingMode: 'local',
      locale: dimensions.locale,
      landingLocale: dimensions.landing_locale,
    } satisfies ToolAnalyticsContext
  }, [locale, pathname])
  const [input, setInput] = useState('')
  const [outputMode, setOutputMode] = useState<OutputMode>('format')
  const [output, setOutput] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [validationMessage, setValidationMessage] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const handleFormat = useCallback(() => {
    if (!input.trim()) {
      setOutput('')
      setError(null)
      setValidationMessage(null)
      return
    }

    setValidationMessage(null)
    const start = performance.now()
    const operation = startToolOperation(analyticsContext)

    const result = formatJson(input)
    if (result.ok) {
      setOutput(result.result)
      setError(null)
      setValidationMessage(t.jsonFormatter.valid)
      operation.success(performance.now() - start)
    } else {
      setOutput('')
      setError(t.jsonFormatter.errors.invalidJson.replace('{error}', result.error))
      setValidationMessage(null)
      operation.error('invalid_input')
    }
  }, [analyticsContext, input, t])

  const handleMinify = useCallback(() => {
    if (!input.trim()) {
      setOutput('')
      setError(null)
      setValidationMessage(null)
      return
    }

    setValidationMessage(null)
    const start = performance.now()
    const operation = startToolOperation(analyticsContext)

    const result = minifyJson(input)
    if (result.ok) {
      setOutput(result.result)
      setError(null)
      setValidationMessage(t.jsonFormatter.valid)
      operation.success(performance.now() - start)
    } else {
      setOutput('')
      setError(t.jsonFormatter.errors.invalidJson.replace('{error}', result.error))
      setValidationMessage(null)
      operation.error('invalid_input')
    }
  }, [analyticsContext, input, t])

  const handleValidate = useCallback(() => {
    if (!input.trim()) {
      setValidationMessage(null)
      setError(null)
      setOutput('')
      return
    }

    const start = performance.now()
    setOutput('')
    const operation = startToolOperation(analyticsContext)
    const result = validateJson(input)

    if (result.ok) {
      setValidationMessage(t.jsonFormatter.valid)
      setError(null)
      operation.success(performance.now() - start)
    } else {
      setValidationMessage(null)
      setError(t.jsonFormatter.errors.invalidJson.replace('{error}', result.error))
      operation.error('invalid_input')
    }
  }, [analyticsContext, input, t])

  const lastRunRef = useRef<string | null>(null)

  const run = useCallback((mode: OutputMode) => {
    lastRunRef.current = `${mode}:${input}`
    if (mode === 'format') {
      handleFormat()
    } else if (mode === 'minify') {
      handleMinify()
    } else {
      handleValidate()
    }
  }, [handleFormat, handleMinify, handleValidate, input])

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      if (lastRunRef.current !== `${outputMode}:${input}`) run(outputMode)
    }, 200)

    return () => window.clearTimeout(timeout)
  }, [input, outputMode, run])

  const handleCopy = useCallback(async () => {
    if (!output) return
    try {
      await navigator.clipboard.writeText(output)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      setCopied(false)
    }
  }, [output])

  const handleClear = useCallback(() => {
    lastRunRef.current = null
    setInput('')
    setOutput('')
    setError(null)
    setValidationMessage(null)
  }, [])

  const inputStats = useMemo(() => {
    const chars = input.length
    const lines = input ? input.split('\n').length : 0
    return { chars, lines }
  }, [input])

  const outputStats = useMemo(() => {
    const chars = output.length
    const lines = output ? output.split('\n').length : 0
    return { chars, lines }
  }, [output])

  return (
    <ToolPageShell toolKey="jsonFormatter" width="wide">
      <CodeWorkspace
        error={error}
        notice={validationMessage}
        controls={(
          <div className="flex flex-wrap items-center gap-2" role="group">
            {([
              ['format', t.jsonFormatter.format],
              ['minify', t.jsonFormatter.minify],
              ['validate', t.jsonFormatter.validate],
            ] as const).map(([value, label]) => (
              <button
                key={value}
                type="button"
                aria-pressed={outputMode === value}
                data-code-run
                onClick={() => {
                  setOutputMode(value)
                  run(value)
                }}
                className={cn(
                  'rounded-md border px-4 py-2 text-sm font-medium transition-colors',
                  outputMode === value
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'bg-card text-muted-foreground hover:text-foreground',
                )}
              >
                {label}
              </button>
            ))}
          </div>
        )}
        input={{
          title: t.jsonFormatter.input,
          value: input,
          onChange: (value) => {
            lastRunRef.current = null
            setInput(value)
            setOutput('')
            setError(null)
            setValidationMessage(null)
          },
          placeholder: t.jsonFormatter.inputPlaceholder,
          ariaLabel: t.jsonFormatter.input,
          clear: { label: t.jsonFormatter.clear, onClick: handleClear },
          stats: [
            {
              label: t.jsonFormatter.characters,
              value: inputStats.chars.toLocaleString(localeCode),
            },
            {
              label: t.jsonFormatter.lines,
              value: inputStats.lines.toLocaleString(localeCode),
            },
          ],
        }}
        output={{
          title: t.jsonFormatter.output,
          value: output,
          readOnly: true,
          placeholder: t.jsonFormatter.outputPlaceholder,
          ariaLabel: t.jsonFormatter.output,
          actions: (
            <>
              <Button variant="outline" size="sm" onClick={handleCopy}>
                {copied ? t.jsonFormatter.copied : t.jsonFormatter.copy}
              </Button>
            </>
          ),
          stats: output && !error
            ? [
              {
                label: t.jsonFormatter.characters,
                value: outputStats.chars.toLocaleString(localeCode),
              },
              {
                label: t.jsonFormatter.lines,
                value: outputStats.lines.toLocaleString(localeCode),
              },
            ]
            : undefined,
        }}
      />

      {/* Info section */}
      <section className="mt-10 space-y-5 border-t pt-8">
        <div>
          <h2 className="text-xl font-semibold tracking-normal">{t.jsonFormatter.howTitle}</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {t.jsonFormatter.how}
          </p>
        </div>
        <div>
          <h2 className="text-xl font-semibold tracking-normal">{t.jsonFormatter.whenTitle}</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {t.jsonFormatter.when}
          </p>
        </div>
      </section>
    </ToolPageShell>
  )
}
